import { Id, Options, Resource, SuccessResponse } from 'services/api';
import {
  GiftCardCreateResponse,
  GiftCardDiscountPayload,
  GiftCardDiscountResponse,
  GiftCardDownloadReportResponse,
  GiftCardListResponse,
  GiftCardProductResponse,
  GiftCardRecipientsCsvResponse,
  GiftCardSummaryResponse,
  GiftCardUploadCsvPayload
} from 'src/services/api/types/giftCard';

import Company from './Company.service';

/**
 * Service for Gift Card in Comadmin
 */
export default class GiftCard extends Company {
  private static instance: GiftCard;

  private constructor(options: Options) {
    /* istanbul ignore next */
    super(options);
  }

  /**
   * This returns the singleton instance of the class
   * @param {Options} options
   */
  public static getInstance(options: Options) {
    if (!this.instance) {
      this.instance = new this(options);
    }

    return this.instance;
  }

  /**
   * Create a gift card order
   * @param {FormData} [payload] formdata
   * key: `csv_recipients` | value: uploaded file
   * key: `payload` | value: payload object stringified
   */
  create(payload: FormData) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order`;

    return this.makeRequest<GiftCardCreateResponse>({
      url,
      method: this._request.Post,
      data: payload,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * Read a gift card by id
   * @param {Id} [id]
   */
  get(id: Id) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order?id=${id}`;
    return this.makeRequest<GiftCardSummaryResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Get a gift card purchase form products
   */
  get_products() {
    const url = `${this.buildUrl(Resource.GiftCard)}/products`;
    return this.makeRequest<GiftCardProductResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Get a list of gift cards
   */
  list(params?: Record<string, any>) {
    const url = `${this.buildUrl(Resource.GiftCard)}/orders`;
    return this.makeRequest<GiftCardListResponse>({
      url,
      method: this._request.Get,
      params
    });
  }

  /**
   * Fetch the discount amount and percentage for the order
   */
  fetchDiscount(payload: GiftCardDiscountPayload) {
    const url = `${this.buildUrl(Resource.GiftCard)}/discount`;
    return this.makeRequest<GiftCardDiscountResponse>({
      url,
      method: this._request.Post,
      data: payload
    });
  }

  /**
   * download gift card recipient csv
   */
  downloadCsvRecipients(id: Id) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order/recipients?id=${id}`;

    return this.makeRequest<GiftCardRecipientsCsvResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Send gift card detail email
   */
  sendGiftCardDetailEmail(id: Id) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order/email?id=${id}`;

    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Post
    });
  }

  /**
   * Get a gift card list report
   */
  downloadReport() {
    const url = `${this.buildUrl(Resource.GiftCard)}/orders/report`;
    return this.makeRequest<GiftCardDownloadReportResponse>({
      url,
      method: this._request.Get
    });
  }

  /**
   * Upload Receipt file for a gift card order
   * @param {GiftCardUploadCsvPayload} [payload] formdata
   * formData key: `payment_receipt` | value: uploaded file
   */
  uploadReceipt(payload: GiftCardUploadCsvPayload) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order/upload-receipt?id=${
      payload.id
    }`;

    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Post,
      data: payload.formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * Upload updated CSV file with correct recipients
   * @param {GiftCardUploadCsvPayload} [payload] formdata
   * formData key: `failed_recipients_reupload` | value: uploaded file
   */
  uploadUpdatedEmailCsv(payload: GiftCardUploadCsvPayload) {
    const url = `${this.buildUrl(Resource.GiftCard)}/order/email?id=${
      payload.id
    }`;

    return this.makeRequest<SuccessResponse>({
      url,
      method: this._request.Post,
      data: payload.formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * download erroneous recipient file
   */
  downloadErroneousCsv(id: Id) {
    const url = `${this.buildUrl(
      Resource.GiftCard
    )}/order/failed-deliveries?id=${id}`;

    return this.makeRequest<GiftCardRecipientsCsvResponse>({
      url,
      method: this._request.Get
    });
  }
}
